import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { Device } from '../devices/devices.interfaces';
import { User } from '../users/users.interfaces';
import {
  AddDeviceRequest,
  AddDeviceToNewInstallationRequest,
  Installation,
  InstallationImp,
  UpdateInstallationRequest,
} from './installations.interfaces';

import { map } from 'rxjs/operators';
import { Globals } from '../../globals';
import { Timezone } from '../timezones/timezones.interfaces';

@Injectable({
  providedIn: 'root',
})

/**
 * Installation REST calls
 */
export class InstallationsService {
  private readonly http: HttpClient = inject(HttpClient);
  private readonly globals: Globals = inject(Globals);

  /**
   * Get all installations for the currently logged in user
   */
  getInstallations(): Observable<InstallationImp[]> {
    const url = `${this.globals.apiRoot}/jwtinstallations`;
    return this.http
      .get<InstallationImp[]>(url)
      .pipe(
        map((data) =>
          data.map((item) => Object.assign(new InstallationImp(), item)),
        ),
      );
  }

  getInstallationsOwned(): Observable<InstallationImp[]> {
    const url = `${this.globals.apiRoot}/jwtinstallations/owned`;
    return this.http
      .get<InstallationImp[]>(url)
      .pipe(
        map((data) =>
          data.map((item) => Object.assign(new InstallationImp(), item)),
        ),
      );
  }

  getInstallationsOwnedAndShared(): Observable<Installation[]> {
    const url = `${this.globals.apiRoot}/jwtinstallations/ownedandshared`;
    return this.http.get<Installation[]>(url);
  }

  /**
   * Get all installations (with devices and network devices) for the currently logged in user
   */
  getInstallationsWithDevicesAndNetworkDevices(): Observable<
    InstallationImp[]
  > {
    const url = `${this.globals.apiRoot}/jwtinstallations/devices/networkdevices`;
    return this.http
      .get<InstallationImp[]>(url)
      .pipe(
        map((data) =>
          data.map((item) => Object.assign(new InstallationImp(), item)),
        ),
      );
  }

  /**
   * Get all installations (with devices and network devices) for the currently logged in user
   */
  getOwnedInstallationsWithDevicesAndNetworkDevices(): Observable<
    InstallationImp[]
  > {
    const url = `${this.globals.apiRoot}/jwtinstallations/owned/devices/networkdevices`;
    return this.http
      .get<InstallationImp[]>(url)
      .pipe(
        map((data) =>
          data.map((item) => Object.assign(new InstallationImp(), item)),
        ),
      );
  }

  /**
   * Get  installations count for the currently logged in user
   */
  getInstallationsCount(): Observable<number> {
    const url = `${this.globals.apiRoot}/jwtinstallations/count`;
    return this.http.get<number>(url);
  }

  /**
   * Get all favorite installations for the currently logged in user
   */
  getFavoriteInstallations(): Observable<InstallationImp[]> {
    const url = `${this.globals.apiRoot}/jwtinstallations/favorite`;
    return this.http
      .get<InstallationImp[]>(url)
      .pipe(
        map((data) =>
          data.map((item) => Object.assign(new InstallationImp(), item)),
        ),
      );
  }

  /**
   * Get installation by id
   * @param id Installation id
   */
  getInstallation(id: number): Observable<InstallationImp> {
    const url = `${this.globals.apiRoot}/jwtinstallations/${id}`;
    return this.http
      .get<InstallationImp>(url)
      .pipe(map((item) => Object.assign(new InstallationImp(), item)));
  }

  /**
   * Get the time zone of the first installation
   * of the owner.
   *
   * If this method gets called by an installer
   * a default timezone id is returned.
   * @returns
   */
  getTimeZoneIdOfFirstInstallation() {
    const url = `${this.globals.apiRoot}/jwtinstallations/timezone`;
    return this.http.get<string>(url);
  }

  /**
   * Get all time zones for an installation
   * @param id Installation id
   * @param isShared if the installation is owned or shared
   * @returns an array of devices with there time zones
   */
  getTimeZonesOfInstallation(
    id: number,
    isShared: boolean,
  ): Observable<Device[]> {
    const url = `${this.globals.apiRoot}/jwtinstallations/${id}/timezones?isShared=${isShared}`;
    return this.http.get<Device[]>(url);
  }

  /**
   * Add installation
   * @param installation Installation
   */
  addInstallation(installation: InstallationImp): Observable<InstallationImp> {
    const url = `${this.globals.apiRoot}/jwtinstallations`;
    return this.http.post<InstallationImp>(url, installation);
  }

  /**
   * Update installation
   * @param installation Installation to update
   */
  updateInstallation(installation: Installation): Observable<void> {
    const url = `${this.globals.apiRoot}/jwtinstallations/${installation.id}`;
    return this.http.put<void>(url, installation);
  }

  updateInstallationOwned(
    id: number,
    request: UpdateInstallationRequest,
  ): Observable<void> {
    const url = `${this.globals.apiRoot}/jwtinstallations/owned/${id}`;
    return this.http.put<void>(url, request);
  }

  /**
   * Update installation license
   * @param installation Installation to update
   */
  updateInstallationLicense(installation: InstallationImp): Observable<void> {
    const url = `${this.globals.apiRoot}/jwtinstallations/${installation.id}/license`;
    return this.http.put<void>(url, installation);
  }

  /**
   * Deliver installation to a user
   * @param id Installation id
   * @param user User to deliver the installation to
   */
  deliverInstallation(id: number, user: User): Observable<void> {
    const url = `${this.globals.apiRoot}/jwtinstallations/${id}/deliver`;
    return this.http.post<void>(url, user);
  }

  /*************** DEVICES *****************/

  /**
   * Get all devices for an installation
   * @param id Installation id
   */
  getDevicesForInstallation(id: number): Observable<Device[]> {
    const url = `${this.globals.apiRoot}/jwtinstallations/${id}/devices`;
    return this.http.get<Device[]>(url);
  }

  /**
   * Get all devices and network devices for an installation
   * @param id Installation id
   */
  getDevicesAndNetworkDevicesForInstallation(id: number): Observable<Device[]> {
    const url = `${this.globals.apiRoot}/jwtinstallations/${id}/devices/networkdevices`;
    return this.http.get<Device[]>(url);
  }

  /**
   * Add a device to installation
   * @param id Installation id
   * @param serialNumber Device serial number
   * @param activationKey Device activation key
   * @param timezone Device timezone
   * @deprecated Use addDeviceToNewInstallation or addDeviceToExistingInstallation
   */
  addDeviceToInstallation(
    id: number,
    serialNumber: string,
    activationKey: string,
    timezone: Timezone,
  ): Observable<Device> {
    // create object to send to the api
    const deviceToAdd = {
      serialNumber: serialNumber,
      activationKey: activationKey,
      timezone: timezone,
    };

    const url = `${this.globals.apiRoot}/jwtinstallations/${id}/devices/`;
    return this.http.post<Device>(url, deviceToAdd);
  }

  /**
   * Delete device from installation
   * @param id Installation id
   * @param deviceId Device Id
   */
  deleteDeviceFromInstallation(id: number, deviceId: number): Observable<void> {
    const url = `${this.globals.apiRoot}/jwtinstallations/${id}/device/${deviceId}`;
    return this.http.delete<void>(url);
  }

  /*************** USERS *****************/

  /**
   * Get installation owner
   * @param id User id
   */
  getOwnerOfInstallation(id: number): Observable<InstallationImp> {
    const url = `${this.globals.apiRoot}/jwtinstallations/${id}/owner`;
    return this.http
      .get<InstallationImp>(url)
      .pipe(map((item) => Object.assign(new InstallationImp(), item)));
  }

  /**
   * Delete installation owner
   * @param id Installation id
   */
  deleteOwnerFromInstallation(id: number): Observable<void> {
    const url = `${this.globals.apiRoot}/jwtinstallations/${id}/owner`;
    return this.http.delete<void>(url);
  }

  /**
   * accept installation by owner
   * @param id Installation id
   * @param userId the user id of the owner
   */
  acceptInstallationByOwner(
    installationId: number,
    userId: number,
  ): Observable<void> {
    const url = `${this.globals.apiRoot}/jwtinstallations/${installationId}/accept/${userId}`;
    return this.http.put<void>(url, {});
  }

  /**
   * check if the instalation hasn't been comfirmed yet
   * @param installationId the id of the installation
   */
  isOwnerComfirmedOfInstallation(installationId: number) {
    const url = `${this.globals.apiRoot}/jwtinstallations/${installationId}/needaccept`;
    return this.http.get<boolean>(url);
  }

  /**
   * get the installer of an instalation
   * @param id the installer id
   */
  getInstallerOfInstalation(installationId: number): Observable<User> {
    const url = `${this.globals.apiRoot}/jwtinstallations/${installationId}/installer`;
    return this.http.get<User>(url);
  }

  /**
   * Get all installers (installer + installer2) of an installation
   * @param installationId Installation id
   */
  getAllInstallersOfInstallation(
    installationId: number,
  ): Observable<{ installer: User | null; installer2: User | null }> {
    const url = `${this.globals.apiRoot}/jwtinstallations/${installationId}/installers`;
    return this.http.get<{ installer: User | null; installer2: User | null }>(
      url,
    );
  }

  /**
   * add an installer to the installation
   *
   * Overloads:
   * - legacy: link by installer code (owners only)
   * - new: invite by email only
   */
  addInstallerToInstallation(
    installationId: number,
    code: string,
    options?: { installerSlot?: 1 | 2 },
  ): Observable<void>;

  addInstallerToInstallation(
    installationId: number,
    payload: {
      email?: string;
      code?: string;
      visualisationAccess?: boolean;
      configurationAccess?: boolean;
    },
    options?: { installerSlot?: 1 | 2 },
  ): Observable<void>;

  addInstallerToInstallation(
    installationId: number,
    codeOrPayload:
      | string
      | {
          email?: string;
          code?: string;
          visualisationAccess?: boolean;
          configurationAccess?: boolean;
        },
    options?: { installerSlot?: 1 | 2 },
  ): Observable<void> {
    const url = `${this.globals.apiRoot}/jwtinstallations/${installationId}/installer`;
    const installerSlot = options?.installerSlot ?? 1;

    if (typeof codeOrPayload === 'string') {
      return this.http.put<void>(url, {
        code: codeOrPayload,
        installerSlot,
      });
    }

    return this.http.put<void>(url, {
      ...codeOrPayload,
      installerSlot,
    });
  }

  /**
   * Delete installation installer
   * @param id Installation id
   */
  deleteInstallerFromInstallation(
    id: number,
    installerSlot: 1 | 2 = 1,
  ): Observable<void> {
    const url = `${this.globals.apiRoot}/jwtinstallations/${id}/installer?installerSlot=${installerSlot}`;
    return this.http.delete<void>(url);
  }

  updateInstallerAccess(
    installationId: number,
    installerSlot: 1 | 2,
    payload: { visualisationAccess: boolean; configurationAccess: boolean },
  ): Observable<void> {
    const url = `${this.globals.apiRoot}/jwtinstallations/${installationId}/installer/access`;
    return this.http.put<void>(url, {
      installerSlot,
      visualisationAccess: payload.visualisationAccess,
      configurationAccess: payload.configurationAccess,
    });
  }

  /**
   * accept installation by installer
   * @param id Installation id
   * @param userId the user id of the installer
   */
  acceptInstallationByInstaller(
    installationId: number,
    userId: number,
  ): Observable<void> {
    const url = `${this.globals.apiRoot}/jwtinstallations/${installationId}/acceptByInstaller/${userId}`;
    return this.http.put<void>(url, {});
  }

  addDeviceToNewInstallation(request: AddDeviceToNewInstallationRequest) {
    const url = `${this.globals.apiRoot}/jwtInstallations/devicesV2`;
    return this.http.post<void>(url, request);
  }

  addDeviceToExistingInstallation(
    installationId: number,
    request: AddDeviceRequest,
  ) {
    const url = `${this.globals.apiRoot}/jwtInstallations/${installationId}/devicesV2`;
    return this.http.post<void>(url, request);
  }

  deleteById(id: number) {
    const url = `${this.globals.apiRoot}/jwtInstallations/${id}`;
    return this.http.delete<void>(url);
  }

  hasUbie(installationId: number): Observable<boolean> {
    const url = `${this.globals.apiRoot}/jwtInstallations/${installationId}/hasubie`;
    return this.http.get<boolean>(url);
  }
}
